import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../utils/api';

export function useAlertData(selectedDevice, selectedInterval, customRange = { start: '', end: '' }) {
    const [alerts, setAlerts] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchAlerts = useCallback(async () => {
        if (!selectedDevice || !selectedInterval) {
            setAlerts([]);
            setMeta(null);
            setLastUpdated(null);
            return;
        }

        if (selectedInterval === 'custom') {
            if (!customRange?.start || !customRange?.end) {
                setAlerts([]);
                setMeta(null);
                setLastUpdated(null);
                return;
            }
        }

        try {
            setLoading(true);
            setError(null);

            const extraParams =
                selectedInterval === 'custom' && customRange?.start && customRange?.end
                    ? { dt_start: customRange.start, dt_end: customRange.end }
                    : undefined;

            const data = await apiClient.getAlertsData(selectedDevice, selectedInterval, extraParams);
            const items = Array.isArray(data?.items) ? data.items : [];

            setAlerts(items);
            setMeta(data?.meta ?? null);
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Error fetching alerts:', err);
            if (err?.status === 403) {
                setError('You do not have permission to view alerts.');
            } else if (err?.status === 401) {
                setError('Session expired. Please sign in again.');
            } else {
                setError(err.message || 'Unknown error fetching alerts');
            }
            setAlerts([]);
            setMeta(null);
            setLastUpdated(null);
        } finally {
            setLoading(false);
        }
    }, [selectedDevice, selectedInterval, customRange?.start, customRange?.end]);

    useEffect(() => {
        fetchAlerts();
    }, [fetchAlerts]);

    return {
        alerts,
        meta,
        loading,
        error,
        lastUpdated,
        refresh: fetchAlerts,
    };
}
